import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MobileBottomNav from './MobileBottomNav';
import ActivationModal from './ActivationModal';
import ActivationAlerts from './ActivationAlerts';
import ForceEmailSetupModal from './ForceEmailSetupModal';
import EmailVerifyForLoggedUser from './EmailVerifyForLoggedUser';
import FloatingActions from './FloatingActions';
import { useNavVisibility } from '../contexts/NavVisibilityContext';
import { 
  Menu, X, LayoutDashboard, Zap, Users, FileText, 
  Settings, MessageCircle, MessageSquare, LogOut, User, Coins, ShoppingCart, Store, ChevronDown, Plus, DollarSign, Mail, AlertCircle, Crown, Sparkles, Ticket, Bell, ChevronRight
} from 'lucide-react';
import { formatCurrency, formatCredits } from '../utils/format';

const ExternalLinks = () => {
  const { api } = useAuth();
  const [settings, setSettings] = useState({ upline_whatsapp: '', telegram_link: '' });
  const [uplineContact, setUplineContact] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchUplineContact();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchUplineContact = async () => {
    try {
      const response = await api.get('/api/users/upline/contact');
      setUplineContact(response.data);
    } catch (error) {
      console.error('Error fetching upline contact:', error);
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    const whatsapp = uplineContact?.whatsapp || settings.upline_whatsapp;
    if (whatsapp) {
      window.open(`https://wa.me/${whatsapp}`, '_blank');
    } else {
      alert('WhatsApp do master não configurado');
    }
  };

  const handleCommunityClick = (e) => {
    e.preventDefault();
    if (settings.telegram_link) {
      window.open(settings.telegram_link, '_blank');
    } else {
      alert('Link da comunidade não configurado');
    }
  };

  return (
    <>
      <button
        onClick={handleContactClick}
        className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13.5px] text-[#BDB5CE] hover:bg-[#1A1626] hover:text-white transition-colors text-left"
      >
        <MessageCircle size={17} className="text-[#45D4A8] flex-shrink-0" />
        <span className="truncate">Contato master{uplineContact?.upline_username && ` (${uplineContact.upline_username})`}</span>
      </button>
      <button
        onClick={handleCommunityClick}
        className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13.5px] text-[#BDB5CE] hover:bg-[#1A1626] hover:text-white transition-colors text-left"
      >
        <MessageSquare size={17} className="text-[#5B9BFF] flex-shrink-0" />
        <span>Comunidade</span>
      </button>
    </>
  );
};

const Layout = ({ children }) => {
  const { user, logout, api, refreshUser } = useAuth();
  const { isNavVisible } = useNavVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const [perfilAberto, setPerfilAberto] = useState(false);
  const [gavetaAberta, setGavetaAberta] = useState(false);   // lateral no celular
  // Ativação rápida pelo botão central da ilha. Fica aqui no Layout porque o
  // modal esconde a barra inferior ao abrir — se morasse lá dentro, sumiria junto.
  const [ativacaoRapida, setAtivacaoRapida] = useState(false);
  const [appsAtivacao, setAppsAtivacao] = useState([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [systemLogo, setSystemLogo] = useState('');
  const [systemName, setSystemName] = useState('Ative App');
  const [showForceEmailSetup, setShowForceEmailSetup] = useState(false);
  const [showEmailVerify, setShowEmailVerify] = useState(false);
  const [emailMasked, setEmailMasked] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/my-store') || location.pathname === '/premium') {
      setStoreMenuOpen(true);
    }
    if (location.pathname === '/admin' || location.pathname === '/smtp-config' || 
        location.pathname === '/premium-admin') {
      setAdminMenuOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user && !user.email && location.pathname !== '/login') {
      setShowForceEmailSetup(true);
    }
  }, [user, location.pathname]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      setSystemLogo(response.data.system_logo || '');
      setSystemName(response.data.system_name || 'Ative App');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', gradient: 'from-purple-500 to-pink-500' },
    { path: '/activations', icon: Zap, label: 'Ativações', gradient: 'from-yellow-500 to-orange-500' },
    { path: '/resellers', icon: Users, label: 'Revendedores', gradient: 'from-blue-500 to-cyan-500' },
    { path: '/buy-credits', icon: ShoppingCart, label: 'Comprar Créditos', gradient: 'from-green-500 to-emerald-500' },
    { 
      label: 'Sua Loja', 
      icon: Store, 
      gradient: 'from-indigo-500 to-purple-500',
      expandable: true,
      subItems: [
        { path: '/my-store', label: 'Configurar Loja', icon: Settings },
        { path: '/payment-methods', label: 'Métodos de Pagamento', icon: DollarSign },
        { path: '/premium', label: 'Premium', icon: Crown },
        { path: '/my-store/stats', label: 'Estatísticas', icon: FileText }
      ]
    },
    { path: '/transactions', icon: FileText, label: 'Logs de Transações', gradient: 'from-gray-500 to-slate-500' },
  ];

  if (user?.role === 'admin') {
    menuItems.push({
      label: 'Admin',
      icon: Settings,
      gradient: 'from-red-500 to-rose-500',
      expandable: true,
      subItems: [
        { path: '/admin', label: 'Admin Geral', icon: Settings },
        { path: '/coupons', label: 'Cupons', icon: Ticket },
        { path: '/announcements', label: 'Avisos', icon: Bell },
        { path: '/smtp-config', label: 'Config. Email', icon: Mail },
        { path: '/premium-admin', label: 'Admin Premium', icon: Crown }
      ]
    });
  }

  useEffect(() => {
    setGavetaAberta(false);   // trocou de tela, fecha a gaveta
  }, [location.pathname]);

  const abrirAtivacaoRapida = async () => {
    setAtivacaoRapida(true);
    if (appsAtivacao.length === 0) {
      try {
        const { data } = await api.get('/api/apps', { params: { limit: 200 } });
        setAppsAtivacao(data.items || []);
      } catch (error) {
        console.error('Erro ao carregar apps:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEmailSetupSuccess = (data) => {
    setShowForceEmailSetup(false);
    setEmailMasked(data.email_masked);
    setShowEmailVerify(true);
  };

  const handleEmailVerifySuccess = async () => {
    setShowEmailVerify(false);
    await refreshUser();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Animated Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      {/* Barra lateral única: marca, usuário, saldos, navegação e perfil */}
      {/* Escurecido atrás da gaveta, no celular */}
      <AnimatePresence>
        {gavetaAberta && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setGavetaAberta(false)}
            className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
          />
        )}
      </AnimatePresence>

      <div className={`fixed top-0 left-0 h-screen w-64 bg-[#13101C] border-r border-[#272034] z-50 flex flex-col transition-transform duration-300 lg:z-30 lg:translate-x-0 ${
        gavetaAberta ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Marca */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-[#272034]">
          {systemLogo ? (
            <img src={systemLogo} alt="" className="w-9 h-9 rounded-[10px] object-cover" />
          ) : (
            <div className="relative w-9 h-9 rounded-[10px] bg-gradient-to-br from-[#A978FF] to-[#6D3BD4] flex items-center justify-center">
              <Sparkles size={18} className="text-white" />
              <span className="absolute inset-0 rounded-[10px] shadow-[0_0_18px_rgba(155,107,255,0.45)] animate-pulse-slow pointer-events-none"></span>
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[14.5px] font-semibold text-white truncate">{systemName}</p>
            <p className="text-[11.5px] text-[#938BA8]">Sistema premium</p>
          </div>
        </div>

        {/* Usuário */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#272034]">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-[9px] object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-[9px] bg-[#211B30] flex items-center justify-center text-[#C9BEE4] font-semibold text-[13px]">
              {user?.username?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-white truncate">{user?.username}</p>
            <p className="text-[11.5px] text-[#938BA8]">{user?.role === 'admin' ? 'Administrador' : 'Usuário'}</p>
          </div>
        </div>

        {/* Saldos */}
        <div className="flex gap-2 px-3 py-3 border-b border-[#272034]">
          <div className="flex-1 relative bg-[#1A1626] border border-[#272034] rounded-[9px] px-3 py-2">
            <p className="text-[10.5px] text-[#938BA8]">Créditos</p>
            <p className="text-[15px] font-semibold text-white tabular-nums">{formatCredits(user?.credits || 0, user?.role)}</p>
            <Link to="/buy-credits" title="Comprar créditos"
              className="absolute top-1.5 right-1.5 w-5 h-5 rounded-md bg-[#9B6BFF]/15 text-[#9B6BFF] flex items-center justify-center hover:bg-[#9B6BFF]/25 transition-colors">
              <Plus size={12} strokeWidth={3} />
            </Link>
          </div>
          <div className="flex-1 bg-[#1A1626] border border-[#272034] rounded-[9px] px-3 py-2">
            <p className="text-[10.5px] text-[#938BA8]">Cashback</p>
            <p className="text-[15px] font-semibold text-white tabular-nums">{formatCredits(user?.saldo_cashback || 0, user?.role)}</p>
          </div>
        </div>

        {/* Navegação */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-2 scrollbar-hide">
          {menuItems.map((item, index) => {
            const aberto = (item.label === 'Sua Loja' && storeMenuOpen) || (item.label === 'Admin' && adminMenuOpen);
            if (item.expandable) {
              return (
                <div key={index}>
                  <button
                    onClick={() => {
                      if (item.label === 'Sua Loja') setStoreMenuOpen(!storeMenuOpen);
                      if (item.label === 'Admin') setAdminMenuOpen(!adminMenuOpen);
                    }}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13.5px] transition-colors ${
                      aberto ? 'bg-[#1A1626] text-white' : 'text-[#BDB5CE] hover:bg-[#1A1626] hover:text-white'
                    }`}
                  >
                    <item.icon size={17} className="opacity-85 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                    <ChevronRight size={14} className={`ml-auto transition-transform ${aberto ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {aberto && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        {item.subItems.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className={`flex items-center gap-2.5 pl-9 pr-2.5 py-2 rounded-[9px] text-[13px] transition-colors ${
                              location.pathname === subItem.path
                                ? 'bg-[#9B6BFF]/15 text-white font-medium'
                                : 'text-[#9990AC] hover:bg-[#1A1626] hover:text-white'
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }
            const ativo = location.pathname === item.path;
            return (
              <Link
                key={index}
                to={item.path}
                className={`relative flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13.5px] transition-colors ${
                  ativo ? 'bg-[#9B6BFF]/15 text-white font-medium' : 'text-[#BDB5CE] hover:bg-[#1A1626] hover:text-white'
                }`}
              >
                {ativo && <span className="absolute -left-2.5 top-1.5 bottom-1.5 w-[3px] rounded-r-[3px] bg-[#9B6BFF]" />}
                <item.icon size={17} className="opacity-85 flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}

          <p className="mt-3.5 mb-1.5 px-2.5 text-[11px] text-[#6E667F]">Suporte</p>
          <ExternalLinks />
        </nav>

        {/* Perfil e sair */}
        <div className="border-t border-[#272034] p-2.5">
          <Link to="/profile"
            className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13px] text-[#BDB5CE] hover:bg-[#1A1626] hover:text-white transition-colors">
            <User size={17} />
            <span>Meu perfil</span>
          </Link>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-[9px] text-[13px] text-[#BDB5CE] hover:bg-[#1A1626] hover:text-[#F87171] transition-colors">
            <LogOut size={17} />
            <span>Sair</span>
          </button>
        </div>
      </div>

      {/* Topo do celular: saldo à esquerda, perfil à direita */}
      {isNavVisible && (
        <div data-mobile-header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#13101C]/95 backdrop-blur-xl border-b border-[#272034]" style={{ willChange: 'transform' }}>
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Link to="/buy-credits" className="flex items-center gap-2 rounded-xl bg-[#1A1626] border border-[#272034] px-3 py-2 active:scale-95 transition-transform">
              <Coins size={15} className="text-[#9B6BFF]" />
              <span className="text-[10.5px] text-[#938BA8]">Créditos</span>
              <span className="text-[14px] font-semibold text-white tabular-nums">{formatCredits(user?.credits || 0, user?.role)}</span>
              <Plus size={13} strokeWidth={3} className="text-[#45D4A8]" />
            </Link>

            <div className="flex items-center gap-1.5 rounded-xl bg-[#1A1626] border border-[#272034] px-3 py-2">
              <Sparkles size={14} className="text-[#45D4A8]" />
              <span className="text-[14px] font-semibold text-white tabular-nums">{formatCredits(user?.saldo_cashback || 0, user?.role)}</span>
            </div>

            {/* Perfil */}
            <div className="ml-auto relative">
              <button
                onClick={() => setPerfilAberto(!perfilAberto)}
                aria-label="Abrir menu do perfil"
                className="w-10 h-10 rounded-full overflow-hidden border border-[#2F2742] active:scale-95 transition-transform"
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#A978FF] to-[#6D3BD4] text-white font-semibold">
                    {user?.username?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {perfilAberto && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setPerfilAberto(false)}
                      className="fixed inset-0 z-40"
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ type: 'spring', damping: 22, stiffness: 340 }}
                      className="absolute right-0 top-12 z-50 w-60 origin-top-right rounded-2xl border border-[#272034] bg-[#1A1626] shadow-[0_18px_50px_rgba(0,0,0,0.6)] overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-[#272034]">
                        <p className="text-[14px] font-semibold text-white truncate">{user?.username}</p>
                        <p className="text-[12px] text-[#938BA8] truncate">{user?.email || (user?.role === 'admin' ? 'Administrador' : 'Revendedor')}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setPerfilAberto(false)}
                        className="flex items-center gap-2.5 px-4 py-3 text-[13.5px] text-[#CFC8DD] active:bg-[#211B30]"
                      >
                        <User size={16} />
                        Editar perfil
                      </Link>
                      <button
                        onClick={() => { setPerfilAberto(false); handleLogout(); }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-[13.5px] text-[#F87171] active:bg-[#211B30]"
                      >
                        <LogOut size={16} />
                        Sair
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 pb-28 lg:pb-0 min-h-screen relative z-10">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>

      {/* Ilha de navegação do celular */}
      {isNavVisible && (
        <MobileBottomNav
          onNovaAtivacao={abrirAtivacaoRapida}
          onAbrirMenu={() => setGavetaAberta(true)}
        />
      )}

      {/* Mesmo modal da tela de Ativações, aberto pelo botão central da ilha */}
      <ActivationModal
        show={ativacaoRapida}
        onClose={() => setAtivacaoRapida(false)}
        apps={appsAtivacao}
        onSuccess={async () => {
          setAtivacaoRapida(false);
          await refreshUser();
          if (location.pathname === '/activations') navigate(0);
        }}
      />

      {/* Avisos em tempo real das ativações */}
      <ActivationAlerts />

      {/* Floating Actions */}
      <FloatingActions />

      {/* Modals */}
      {showForceEmailSetup && (
        <ForceEmailSetupModal onSuccess={handleEmailSetupSuccess} />
      )}
      {showEmailVerify && (
        <EmailVerifyForLoggedUser
          emailMasked={emailMasked}
          onSuccess={handleEmailVerifySuccess}
        />
      )}
    </div>
  );
};

export default Layout;